import { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Grid, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGridPremium as DataGrid, GridColDef } from '@mui/x-data-grid-premium';
import { sort } from 'fast-sort';
import {
  EstateInputDataFormatListOption,
  EstateInputDataFormatListOption_FieldsFragment,
  EstateInputDataFormatListOption_GetEstateInputDataFormatListOptionsByEstateInputDataFormatIdDocument,
  useEstateInputDataFormatListOption_GetEstateInputDataFormatListOptionsByEstateInputDataFormatIdQuery,
  useEstateInputDataFormatListOption_UpdateEstateInputDataFormatListOptionMutation,
} from 'queries';
import { ChevronRight } from 'ts-react-feather-icons';

import GridRowOrderAction from 'common/grid-row-order-action';
import HomeLayout from 'layouts/home-layout';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';

import CreateEstateInputDataFormatListOptionModal from './components/create-estate-input-data-format-list-option-modal';
import EditEstateInputDataFormatListOptionModal from './components/edit-estate-input-data-format-list-option-modal';
import EstateInputDataFormatListOptionActions from './components/estate-input-data-format-list-option-actions';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  link: {
    cursor: 'pointer',
  },
  header: {
    marginRight: '10px',
  },
}));

export default function ManageEstateInputDataFormatListOption() {
  const classes = useStyles();
  const history = useHistory();

  const { estateInputDataFormatId } = useParams<Record<string, string>>();

  const [selectedEstateInputDataFormatListOption, setSelectedEstateInputDataFormatListOption] =
    useState<EstateInputDataFormatListOption | undefined>(undefined);

  const [
    showEditEstateInputDataFormatListOptionModal,
    setShowEditEstateDataInputFormatListOptionModal,
  ] = useState<boolean>(false);

  const [
    showCreateEstateInputDataFormatListOptionModal,
    setShowCreateEstateInputDataFormatListOptionModal,
  ] = useState<boolean>(false);

  const [updateEstateInputDataFormatListOption] =
    useEstateInputDataFormatListOption_UpdateEstateInputDataFormatListOptionMutation({
      refetchQueries: [
        {
          query:
            EstateInputDataFormatListOption_GetEstateInputDataFormatListOptionsByEstateInputDataFormatIdDocument,
          variables: { input: { estateInputDataFormatId } },
        },
      ],
      onError: (e) => {
        showAlert({
          message: 'Error re-ordering list option: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const handleOnRowOrderChange = async (
    estateInputDataFormatListOption: EstateInputDataFormatListOption_FieldsFragment,
    newOrder: number,
  ) => {
    updateEstateInputDataFormatListOption({
      variables: {
        input: {
          id: estateInputDataFormatListOption.id,
          sortOrder: newOrder,
        },
      },
    });
  };

  const {
    data: estateInputDataFormatListOptionData,
    loading: estateInputDataFormatListOptionDataLoading,
  } =
    useEstateInputDataFormatListOption_GetEstateInputDataFormatListOptionsByEstateInputDataFormatIdQuery(
      {
        variables: { input: { estateInputDataFormatId: estateInputDataFormatId } },
      },
    );

  const columns: GridColDef<EstateInputDataFormatListOption_FieldsFragment>[] = [
    {
      disableColumnMenu: true,
      field: 'order',
      filterable: false,
      flex: 0.5,
      headerName: 'Order',
      renderCell: ({ row }) => {
        return (
          <GridRowOrderAction
            disableDown={row.sortOrder >= sortedRows.length}
            disableUp={row.sortOrder === 1}
            handleDownClick={() => {
              handleOnRowOrderChange(row, row.sortOrder + 1);
            }}
            handleUpClick={() => {
              handleOnRowOrderChange(row, row.sortOrder - 1);
            }}
          />
        );
      },
    },
    {
      disableColumnMenu: true,
      field: 'name',
      filterable: true,
      flex: 2,
      headerName: 'Name',
      renderCell: ({ row }) => {
        return row.label;
      },
    },
    {
      disableColumnMenu: true,
      field: 'value',
      filterable: false,
      flex: 2,
      headerName: 'Value',
      renderCell: ({ row }) => {
        return row.value;
      },
    },
    {
      disableColumnMenu: true,
      field: 'actions',
      filterable: false,
      flex: 0.5,
      headerName: 'Actions',
      renderCell: ({ row }) => {
        return (
          <EstateInputDataFormatListOptionActions
            estateInputDataFormatListOption={row}
            setShowEditEstateInputDataFormatListOptionModal={
              setShowEditEstateDataInputFormatListOptionModal
            }
            setSelectedEstateInputDataFormatListOption={setSelectedEstateInputDataFormatListOption}
          />
        );
      },
    },
  ];

  const rows = [];

  const estateInputDataFormatListOptions = estateInputDataFormatListOptionData
    ? estateInputDataFormatListOptionData.data
    : [];

  if (estateInputDataFormatListOptions) {
    for (const estateInputDataFormatListOption of estateInputDataFormatListOptions) {
      rows.push(estateInputDataFormatListOption);
    }
  }

  const sortedRows = sort(rows).by([{ asc: (row) => row.sortOrder }]);

  return (
    <HomeLayout showPortalName={false}>
      <>
        {showEditEstateInputDataFormatListOptionModal &&
          selectedEstateInputDataFormatListOption && (
            <EditEstateInputDataFormatListOptionModal
              estateInputDataFormatId={estateInputDataFormatId}
              showModal={showEditEstateInputDataFormatListOptionModal}
              setShowModal={setShowEditEstateDataInputFormatListOptionModal}
              setSelectedEstateInputDataFormatListOption={
                setSelectedEstateInputDataFormatListOption
              }
              selectedEstateInputDataFormatListOption={selectedEstateInputDataFormatListOption}
            />
          )}
        {showCreateEstateInputDataFormatListOptionModal && (
          <CreateEstateInputDataFormatListOptionModal
            estateInputDataFormatId={estateInputDataFormatId}
            showModal={showCreateEstateInputDataFormatListOptionModal}
            setShowModal={setShowCreateEstateInputDataFormatListOptionModal}
          />
        )}
        <div className={classes.container}>
          <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
            <Typography className={classes.title} variant="h5" style={{ flex: 2 }}>
              <span
                onClick={() => {
                  history.push(`/home`);
                }}
              >
                <span className={classes.routeIconContainer}>
                  <img src="/home-icon.svg" alt="home-icon" />
                </span>
                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
              </span>
              <span
                className={classes.link}
                onClick={() => {
                  history.push(`/home/manage-required-information-format`);
                }}
              >
                Required Information Format
              </span>
              <span>
                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
                Data Format List Options
              </span>
            </Typography>
            <div style={{ flex: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ marginTop: '3px', padding: '2px 10px' }}
                onClick={() => {
                  setShowCreateEstateInputDataFormatListOptionModal(true);
                }}
              >
                + Create New List Option
              </Button>
            </div>
          </div>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <div style={{ width: '100%' }}>
                <DataGrid
                  loading={
                    estateInputDataFormatListOptionDataLoading &&
                    !estateInputDataFormatListOptionData
                  }
                  className={classes.root}
                  columns={columns}
                  rows={sortedRows}
                  disableColumnReorder
                  disableRowSelectionOnClick
                  autoHeight={true}
                  rowHeight={65}
                  pagination
                  initialState={{
                    pagination: { paginationModel: { pageSize: 25 } },
                  }}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    </HomeLayout>
  );
}
